import { ref, computed } from 'vue';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { CommentInsert, CommentUpdate } from '@/types/supabase';
import { IComment, ICommentCreate, ICommentUpdate, mapSupabaseCommentToIComment } from '@/types/comment';
import { useAuth } from '@/composables/useAuth';

/**
 * Composable for managing comments
 */
export function useComments() {
  const comments = ref<IComment[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);
  
  // Get the current user's profile
  const { user } = useAuth();
  const currentUserId = computed(() => user.value?.id || null);

  /**
   * Fetch comments for a specific reference
   * @param referenceId The ID of the referenced entity
   */
  async function fetchComments(referenceId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Fetching comments for reference', LogCategory.DATABASE, {
        referenceId,
        referenceIdType: typeof referenceId,
        referenceIdLength: referenceId?.length || 0
      });
      
      // Validate referenceId
      if (!referenceId) {
        logger.error('Invalid referenceId provided', LogCategory.DATABASE, { referenceId });
        error.value = new Error('Invalid reference ID');
        comments.value = [];
        return comments.value;
      }
      
      // Fetch comments from Supabase
      logger.info('Executing Supabase query', LogCategory.DATABASE, {
        table: 'comments',
        filter: { reference_id: referenceId }
      });
      
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: false });
      
      if (commentsError) {
        logger.error('Error fetching comments', LogCategory.DATABASE, {
          error: commentsError,
          message: commentsError.message,
          details: commentsError.details,
          hint: commentsError.hint,
          code: commentsError.code
        });
        throw commentsError;
      }
      
      logger.info('Comments data received from Supabase', LogCategory.DATABASE, {
        count: commentsData?.length || 0,
        firstComment: commentsData && commentsData.length > 0 ? {
          id: commentsData[0].id,
          reference_id: commentsData[0].reference_id,
          created_at: commentsData[0].created_at
        } : null
      });
      
      // Map Supabase comments to IComment interface
      comments.value = commentsData.map(mapSupabaseCommentToIComment).reverse();
      
      logger.info(`Fetched ${comments.value.length} comments`, LogCategory.DATABASE);
      
      return comments.value;
    } catch (err) {
      logger.error('Error in fetchComments', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      
      // Return empty comments array instead of throwing
      comments.value = [];
      return comments.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Create a new comment
   * @param commentData The comment data to create
   */
  async function createComment(commentData: ICommentCreate) {
    loading.value = true;
    error.value = null;
    
    try {
      // Check if user ID is provided
      if (!commentData.created_by) {
        logger.error('No user ID provided for comment creation', LogCategory.DATABASE);
        error.value = new Error('User ID is required to create a comment');
        return null;
      }
      
      logger.info('Creating new comment', LogCategory.DATABASE, {
        referenceId: commentData.reference_id,
        referenceIdType: typeof commentData.reference_id,
        referenceIdLength: commentData.reference_id?.length || 0,
        createdBy: commentData.created_by,
        createdByLength: commentData.created_by.length,
        content: commentData.content.substring(0, 50) + (commentData.content.length > 50 ? '...' : '')
      });
      
      // Prepare the comment data
      const newComment: CommentInsert = {
        content: commentData.content,
        reference_id: commentData.reference_id,
        created_by: commentData.created_by || currentUserId.value || '',
        created_at: new Date().toISOString()
      };
      
      // Insert the comment into Supabase
      const { data: insertedComment, error: insertError } = await supabase
        .from('comments')
        .insert(newComment)
        .select()
        .single();
      
      if (insertError) {
        logger.error('Error creating comment', LogCategory.DATABASE, { error: insertError });
        throw insertError;
      }
      
      if (!insertedComment) {
        throw new Error('Failed to create comment: No data returned');
      }
      
      // Map the inserted comment to IComment interface
      const comment = mapSupabaseCommentToIComment(insertedComment);
      
      // Add the new comment to the comments array
      comments.value = [comment, ...comments.value].reverse();
      
      logger.info('Comment created successfully', LogCategory.DATABASE, {
        commentId: comment.id,
        referenceId: comment.reference_id,
        commentsCount: comments.value.length
      });
      
      return comment;
    } catch (err) {
      logger.error('Error in createComment', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Update an existing comment
   * @param commentData The comment data to update
   */
  async function updateComment(commentData: ICommentUpdate) {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Updating comment', LogCategory.DATABASE, { commentId: commentData.id });
      
      // Prepare the update data
      const updateData: CommentUpdate = {
        content: commentData.content
      };
      
      // Update the comment in Supabase
      const { data: updatedComment, error: updateError } = await supabase
        .from('comments')
        .update(updateData)
        .eq('id', commentData.id)
        .select()
        .single();
      
      if (updateError) {
        logger.error('Error updating comment', LogCategory.DATABASE, { error: updateError });
        throw updateError;
      }
      
      if (!updatedComment) {
        throw new Error('Failed to update comment: No data returned');
      }
      
      // Map the updated comment to IComment interface
      const comment = mapSupabaseCommentToIComment(updatedComment);
      
      // Update the comment in the comments array
      const index = comments.value.findIndex(c => c.id === comment.id);
      if (index !== -1) {
        comments.value[index] = comment;
      }
      
      logger.info('Comment updated successfully', LogCategory.DATABASE, { commentId: comment.id });
      
      return comment;
    } catch (err) {
      logger.error('Error in updateComment', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Delete a comment
   * @param commentId The ID of the comment to delete
   */
  async function deleteComment(commentId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Deleting comment', LogCategory.DATABASE, { commentId });
      
      // Delete the comment from Supabase
      const { error: deleteError } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);
      
      if (deleteError) {
        logger.error('Error deleting comment', LogCategory.DATABASE, { error: deleteError });
        throw deleteError;
      }
      
      // Remove the comment from the comments array
      comments.value = comments.value.filter(c => c.id !== commentId);
      
      logger.info('Comment deleted successfully', LogCategory.DATABASE, { commentId });
      
      return true;
    } catch (err) {
      logger.error('Error in deleteComment', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  return {
    comments,
    loading,
    error,
    fetchComments,
    createComment,
    updateComment,
    deleteComment
  };
}